import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea", "button", "status", "progress"]
  static values = { profileId: String }

  connect() {
    this.isGenerating = false
    this.fullText = ""
    this.subscription = null
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  startGeneration() {
    if (this.isGenerating) return
    
    this.isGenerating = true
    this.fullText = ""
    this.updateUI(true)
    
    // Show streaming container
    const streamingContainer = document.getElementById(`profile_summary_stream_${this.profileIdValue}`)
    if (streamingContainer) {
      streamingContainer.classList.remove('hidden')
    }
    
    // Hide apply button initially
    const applyButton = document.getElementById(`apply_text_button_${this.profileIdValue}`)
    if (applyButton) {
      applyButton.classList.add('hidden')
    }
    
    // Synchrone Generierung (Mistral) statt ActionCable-Streaming: die Cable-
    // Verbindung wurde serverseitig abgewiesen (reject_unauthorized_connection),
    // der 'connected'-Callback feuerte nie -> Spinner haengte endlos.
    // generate_profile_summary erzeugt den Text direkt und liefert einen
    // Turbo-Stream, der den Container mit Ergebnis/Fehler ersetzt und den Text
    // (via Completion-Partial) in den Trix-Editor laedt.
    fetch(`/backend/ai_streaming/generate_profile_summary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ profile_id: this.profileIdValue })
    })
    .then(response => response.json().then(data => ({ ok: response.ok, data })))
    .then(({ ok, data }) => {
      if (!ok || data.status !== 'success' || !data.text) {
        throw new Error(data && data.error ? data.error : 'Unbekannter Fehler')
      }
      // Generierten Text direkt in den Trix-Editor + versteckten Feldwert laden,
      // damit er sichtbar ist UND beim Speichern erhalten bleibt.
      this.fullText = data.text
      this.loadTextIntoEditor(data.text)
      this.isGenerating = false
      this.updateUI(false)
      const streamingContainer = document.getElementById(`profile_summary_stream_${this.profileIdValue}`)
      if (streamingContainer) streamingContainer.classList.add('hidden')
      this.showSuccess('KI-Profiltext generiert und in den Editor übernommen.')
    })
    .catch(error => {
      console.error('Error generating profile summary:', error)
      this.handleError({ detail: { error: error.message } })
    })
  }

  // Laedt HTML-Text in den Trix-Editor der Profilzusammenfassung; Trix synchronisiert
  // den Inhalt automatisch ins versteckte ActionText-Feld (fuer den Form-Save).
  loadTextIntoEditor(html) {
    let trixEditor = null
    let hiddenInput = null

    if (this.hasTextareaTarget) {
      trixEditor = this.textareaTarget.querySelector('trix-editor')
      hiddenInput = this.textareaTarget.querySelector('input[type="hidden"]')
    }
    if (!trixEditor) {
      const form = this.element.closest('form')
      if (form) {
        trixEditor = form.querySelector('trix-editor')
        hiddenInput = form.querySelector('input[type="hidden"][name*="[summary]"]')
      }
    }

    if (trixEditor && trixEditor.editor) {
      trixEditor.editor.loadHTML(html)
    } else if (hiddenInput) {
      hiddenInput.value = html
      hiddenInput.dispatchEvent(new Event('input', { bubbles: true }))
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // This method is called when the streaming starts
  startStreaming() {
    this.isGenerating = true
    this.fullText = ""
    this.updateUI(true)
    
    // Show the streaming container
    const streamingContainer = document.getElementById(`profile_summary_stream_${this.profileIdValue}`)
    if (streamingContainer) {
      streamingContainer.classList.remove('hidden')
    }
  }

  // This method is called for each chunk
  addChunk(event) {
    const chunk = event.detail.chunk
    if (chunk) {
      this.fullText += chunk
      // Update preview text (not Trix editor)
      this.updateStreamingText()
    }
  }
  
  clearTextarea() {
    if (this.hasTextareaTarget) {
      const trixEditor = this.textareaTarget.querySelector('trix-editor')
      if (trixEditor && trixEditor.editor) {
        trixEditor.editor.loadHTML('')
      }
    }
  }
  
  updateTextareaIncremental(chunk) {
    if (this.hasTextareaTarget && chunk) {
      const trixEditor = this.textareaTarget.querySelector('trix-editor')
      if (trixEditor && trixEditor.editor) {
        try {
          // Get the document and its length
          const document = trixEditor.editor.getDocument()
          const length = document.getLength()
          
          // Move cursor to the end of the document
          trixEditor.editor.setSelectedRange([length - 1, length - 1])
          
          // Insert the chunk at the end
          trixEditor.editor.insertString(chunk)
          
          // Also update the hidden input field for form submission
          const hiddenInput = this.textareaTarget.querySelector('input[type="hidden"]')
          if (hiddenInput) {
            // Convert plain text to HTML for Trix (preserve line breaks)
            const htmlText = this.fullText.replace(/\n/g, '<br>')
            hiddenInput.value = htmlText
          }
        } catch (error) {
          console.error('Error inserting chunk into Trix:', error)
          // Fallback: update the entire textarea
          this.updateTextarea()
        }
      } else {
        // If Trix editor is not ready, update the hidden input directly
        const hiddenInput = this.textareaTarget.querySelector('input[type="hidden"]')
        if (hiddenInput) {
          const htmlText = this.fullText.replace(/\n/g, '<br>')
          hiddenInput.value = htmlText
        }
      }
    }
  }

  // This method is called when streaming is complete
  completeStreaming(event) {
    this.isGenerating = false
    this.updateUI(false)
    
    // Get generated text from event or use fullText
    if (event.detail && event.detail.text) {
      this.fullText = event.detail.text
    }
    
    // Update preview text one final time
    this.updateStreamingText()
    
    // Unsubscribe from channel
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    
    // Show apply button
    const applyButton = document.getElementById(`apply_text_button_${this.profileIdValue}`)
    if (applyButton) {
      applyButton.classList.remove('hidden')
    }
    
    // Show success message
    this.showSuccess('KI-Profiltext erfolgreich generiert! Klicken Sie auf "In Editor übernehmen" um den Text zu verwenden.')
  }
  
  // Apply generated text to Trix editor
  applyToEditor(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (!this.fullText) {
      this.showError('Kein Text zum Übernehmen vorhanden.')
      return
    }
    
    // Find Trix editor - try multiple strategies
    let trixEditor = null
    let hiddenInput = null
    
    // Strategy 1: Find via textareaTarget
    if (this.hasTextareaTarget) {
      trixEditor = this.textareaTarget.querySelector('trix-editor')
      hiddenInput = this.textareaTarget.querySelector('input[type="hidden"]')
    }
    
    // Strategy 2: Find via profile ID in the form
    if (!trixEditor) {
      const form = this.element.closest('form')
      if (form) {
        // Look for trix-editor associated with summary field
        const summaryField = form.querySelector('[name*="[summary]"]')
        if (summaryField) {
          const trixContainer = summaryField.closest('.trix-wrapper') || summaryField.parentElement
          trixEditor = trixContainer?.querySelector('trix-editor')
          hiddenInput = summaryField
        }
      }
    }
    
    // Strategy 3: Find any trix-editor in the form (fallback)
    if (!trixEditor) {
      const form = this.element.closest('form')
      if (form) {
        trixEditor = form.querySelector('trix-editor')
        hiddenInput = form.querySelector('input[type="hidden"][name*="[summary]"]')
      }
    }
    
    // Convert plain text to HTML if needed (Trix expects HTML)
    // Clean up the text first - remove leading/trailing whitespace
    let cleanedText = this.fullText.trim()
    
    // Convert multiple newlines (2+) to paragraph breaks
    // Split by double or more newlines to create paragraphs
    let htmlText = cleanedText
      // First, normalize multiple newlines to exactly 2 newlines
      .replace(/\n{3,}/g, '\n\n')
      // Split by double newlines to create paragraphs
      .split(/\n\n+/)
      // Clean each paragraph and wrap in <p> tags
      .map(paragraph => {
        // Remove single newlines within paragraphs (convert to spaces)
        const cleaned = paragraph.trim().replace(/\n/g, ' ')
        // Remove multiple spaces
        return cleaned.replace(/\s+/g, ' ')
      })
      // Filter out empty paragraphs
      .filter(p => p.length > 0)
      // Wrap each paragraph in <p> tags
      .map(p => `<p>${p}</p>`)
      // Join paragraphs
      .join('')
    
    // If no paragraphs were created, wrap the whole text
    if (!htmlText || htmlText.length === 0) {
      const singleLine = cleanedText.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()
      htmlText = `<p>${singleLine}</p>`
    }
    
    // Try to update Trix editor
    if (trixEditor && trixEditor.editor) {
      try {
        trixEditor.editor.loadHTML(htmlText)
        
        // Also update the hidden input field
        if (hiddenInput) {
          hiddenInput.value = htmlText
          hiddenInput.dispatchEvent(new Event('input', { bubbles: true }))
        }
        
        // Hide streaming container and apply button
        const streamingContainer = document.getElementById(`profile_summary_stream_${this.profileIdValue}`)
        if (streamingContainer) {
          streamingContainer.classList.add('hidden')
        }
        const applyButton = document.getElementById(`apply_text_button_${this.profileIdValue}`)
        if (applyButton) {
          applyButton.classList.add('hidden')
        }
        
        this.showSuccess('Text wurde in den Editor übernommen!')
        return
      } catch (error) {
        console.error('Error loading HTML into Trix:', error)
      }
    }
    
    // Fallback: Update hidden input directly
    if (hiddenInput) {
      hiddenInput.value = htmlText
      hiddenInput.dispatchEvent(new Event('input', { bubbles: true }))
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }))
      
      // Hide streaming container and apply button
      const streamingContainer = document.getElementById(`profile_summary_stream_${this.profileIdValue}`)
      if (streamingContainer) {
        streamingContainer.classList.add('hidden')
      }
      const applyButton = document.getElementById(`apply_text_button_${this.profileIdValue}`)
      if (applyButton) {
        applyButton.classList.add('hidden')
      }
      
      this.showSuccess('Text wurde in den Editor übernommen!')
    } else {
      this.showError('Editor nicht gefunden. Bitte Seite neu laden.')
    }
  }

  // This method is called when there's an error
  handleError(event) {
    this.isGenerating = false
    this.updateUI(false)
    
    // Unsubscribe from channel
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    
    // Hide streaming container
    const streamingContainer = document.getElementById(`profile_summary_stream_${this.profileIdValue}`)
    if (streamingContainer) {
      streamingContainer.classList.add('hidden')
    }
    
    this.showError('Fehler bei der KI-Generierung: ' + (event.detail?.error || 'Unbekannter Fehler'))
  }
  
  updateStreamingText() {
    const streamingText = document.getElementById(`streaming_text_${this.profileIdValue}`)
    if (streamingText) {
      streamingText.textContent = this.fullText
    }
  }

  updateTextarea() {
    if (this.hasTextareaTarget && this.fullText) {
      // Update the trix editor if it exists
      const trixEditor = this.textareaTarget.querySelector('trix-editor')
      if (trixEditor && trixEditor.editor) {
        // Convert plain text to HTML if needed (Trix expects HTML)
        const htmlText = this.fullText.replace(/\n/g, '<br>')
        trixEditor.editor.loadHTML(htmlText)
      } else {
        // Fallback: wait for Trix to be ready
        setTimeout(() => {
          const trixEditor = this.textareaTarget.querySelector('trix-editor')
          if (trixEditor && trixEditor.editor) {
            const htmlText = this.fullText.replace(/\n/g, '<br>')
            trixEditor.editor.loadHTML(htmlText)
          } else if (this.textareaTarget.tagName === 'TEXTAREA') {
            this.textareaTarget.value = this.fullText
          }
        }, 100)
      }
    }
  }

  updateUI(generating) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = generating
      this.buttonTarget.innerHTML = generating 
        ? '<i class="fas fa-spinner fa-spin mr-2"></i>Generiere...' 
        : '<i class="fas fa-magic mr-2"></i>KI generieren'
    }

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = generating 
        ? 'KI generiert Profiltext...' 
        : ''
    }

    if (this.hasProgressTarget) {
      this.progressTarget.style.display = generating ? 'block' : 'none'
    }
  }

  showError(message) {
    alert(message) // Simple fallback
  }

  showSuccess(message) {
    // You can implement a toast notification here
  }

  // Focus the Trix editor
  focusEditor(event) {
    event.preventDefault()
    event.stopPropagation()
    
    // Try multiple strategies to find the Trix editor
    let trixEditor = null
    
    // Strategy 1: Find via textareaTarget
    if (this.hasTextareaTarget) {
      trixEditor = this.textareaTarget.querySelector('trix-editor')
    }
    
    // Strategy 2: Find via profile ID in the form
    if (!trixEditor) {
      const form = this.element.closest('form')
      if (form) {
        // Look for trix-editor associated with summary field
        const summaryField = form.querySelector('[name*="[summary]"]')
        if (summaryField) {
          const trixContainer = summaryField.closest('.trix-wrapper') || summaryField.parentElement
          trixEditor = trixContainer?.querySelector('trix-editor')
        }
      }
    }
    
    // Strategy 3: Find any trix-editor in the form (fallback)
    if (!trixEditor) {
      const form = this.element.closest('form')
      if (form) {
        trixEditor = form.querySelector('trix-editor')
      }
    }
    
    // Strategy 4: Global search (last resort)
    if (!trixEditor) {
      trixEditor = document.querySelector('trix-editor')
    }
    
    if (trixEditor) {
      trixEditor.focus()
    } else {
      console.warn('Trix editor not found for focusing')
    }
  }
}
